import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { ReactComponent as SortUpIcon } from 'img/sort_up.svg'
import { ReactComponent as SortDownIcon } from 'img/sort_down.svg'
import strings from 'utils/strings'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import { prop, propOr } from 'ramda'
import { Icon } from 'components/patterns'
import {
  getVehicleComments,
  getVehicleTempComment,
  getVehicleType,
} from 'selectors/truck.js'
import { addVehicleComment, setVehicleTempComment } from 'actions/truck'
import { getElapsedTime } from 'utils/utils'
import { ASC, DESC } from 'utils/constants'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import { userMayAddComment } from 'utils/authorization'
import { userRightsSelector } from 'selectors/authorization'
import {
  DrawerTitle,
  FlexHeaderWrapper,
  SaveCommentBtn,
  DrawerContentWrapper,
  AddNewCommentSection,
  StyledTextArea,
  CommentsSection,
  CommentsSectionHeader,
  TotalCommentsCountText,
  SortButton,
  CommentsList,
  CommentsListItem,
  CommentHeader,
  CommentTitleWrapper,
  CommentTitle,
  CommentPostedTimeString,
  CommentString,
} from './CommentsDrawer.style'

const getSortedComments = (comments, order) =>
  comments.sort((a, b) => {
    if (order === DESC) {
      return new Date(b.date) - new Date(a.date)
    }
    return new Date(a.date) - new Date(b.date)
  })

const CommentsDrawer = ({ location }) => {
  const pathname = propOr('', 'pathname', location)
  const serialNumber = prop(2, pathname.split('/'))
  const summaryPath = `/vehicles/${serialNumber}/summary`

  const dispatch = useDispatch()
  const comments = useSelector(getVehicleComments, { serialNumber }) || []
  const tempComment = useSelector(getVehicleTempComment, { serialNumber }) || ''
  const vehicleType = useSelector(getVehicleType, { serialNumber })
  const appRights = useSelector(userRightsSelector)

  const [commentsLengthBeforeSubmit, setCommentsLength] = useState(
    comments.length
  )
  //initialize the new comment as empty so as to be safe for null
  const [newComment, setNewComment] = useState(
    tempComment ?? strings.empty_string
  )
  const [isSaving, setIsSaving] = useState(false)
  const [sortOrder, setSortOrder] = useState(DESC)

  useEffect(() => {
    if (comments.length > commentsLengthBeforeSubmit) {
      setCommentsLength(comments.length)
      setIsSaving(false)
      setNewComment('')
      dispatch(setVehicleTempComment({ serialNumber, commentString: null }))
    }
  }, [comments.length, commentsLengthBeforeSubmit, serialNumber, dispatch])

  useEffect(() => {
    if (!pathname.includes(summaryPath)) {
      dispatch(setSelectedIdWithDrawer(null))
      dispatch(setVehicleTempComment({ serialNumber, commentString: null }))
    }
  }, [pathname, dispatch, serialNumber, summaryPath])

  //handle the onChange event of the Vehicle Note Textbox
  const vehicleNoteTextChangeHandler = (e) => {
    const vehicleTextBoxNoteValue =
      e.currentTarget.value ?? strings.empty_string
    setNewComment(vehicleTextBoxNoteValue)
    dispatch(
      setVehicleTempComment({
        serialNumber,
        commentString: vehicleTextBoxNoteValue,
      })
    )
  }

  const handleOnCommentSaveClick = () => {
    // trim before saving so no leading or trailing whitespaces are added
    const trimmedComment = newComment.trim()

    // do not save empty or whitespace notes
    if (!trimmedComment) return
    setIsSaving(true)
    dispatch(
      addVehicleComment({
        serialNumber,
        comment: {
          eventNotes: trimmedComment,
          eventType: 'notes',
          objectType: 'vehicle',
          SubType: 'vehicle',
          objectUri: `/vehicles/${serialNumber}/summary`,
        },
      })
    )
  }

  const sortHandler = () => {
    setSortOrder(sortOrder === ASC ? DESC : ASC)
  }

  const isSaveCommentDisabled = !newComment.length
  const sortText =
    sortOrder === DESC
      ? strings.comments_sort_by_newest_first
      : strings.comments_sort_by_oldest_first
  const SortIcon = sortOrder === DESC ? SortUpIcon : SortDownIcon

  return (
    <DrawerContentWrapper className="comments-drawer">
      <FlexHeaderWrapper>
        <Icon
          icon="comments"
          width={23}
          height={18}
          fill="#0051c4"
          viewBox="0 0 23 18"
        />
        <DrawerTitle>
          {serialNumber} {strings.comments}
        </DrawerTitle>
      </FlexHeaderWrapper>
      {userMayAddComment(appRights, vehicleType) && (
        <AddNewCommentSection>
          <StyledTextArea
            placeholder={strings.add_vehicle_comment_placeholder}
            value={newComment}
            onChange={vehicleNoteTextChangeHandler}
            type="textarea"
            width="100%"
            autosize={{ minRows: 1, maxRows: 6 }}
            maxLength={1000}
            charsRemainingNumberOnly
          />
          <SaveCommentBtn
            loading={isSaving}
            disabled={isSaveCommentDisabled}
            type="primary"
            onClick={handleOnCommentSaveClick}
          >
            {!isSaving && (
              <Icon
                icon="save"
                size={14}
                fill={isSaveCommentDisabled ? 'rgba(7, 7, 15, 0.4)' : 'white'}
              />
            )}
            {strings.save_comment}
          </SaveCommentBtn>
        </AddNewCommentSection>
      )}
      <CommentsSection>
        <CommentsSectionHeader>
          <TotalCommentsCountText>
            {comments?.length} {strings.comments}
          </TotalCommentsCountText>
          <SortButton
            type="tertiary"
            onClick={sortHandler}
            disabled={comments?.length === 0}
          >
            <span>{sortText}</span>
            <SortIcon />
          </SortButton>
        </CommentsSectionHeader>
        <CommentsList>
          {getSortedComments(comments, sortOrder).map((item) => (
            <CommentsListItem key={item.id}>
              <CommentHeader>
                <CommentTitleWrapper>
                  <CommentTitle>{item.author}</CommentTitle>
                  <CommentPostedTimeString>
                    {strings.comment_posted_time_string(
                      getElapsedTime(item.date)
                    )}
                  </CommentPostedTimeString>
                </CommentTitleWrapper>
              </CommentHeader>
              <CommentString>{item.eventNotes}</CommentString>
            </CommentsListItem>
          ))}
        </CommentsList>
      </CommentsSection>
    </DrawerContentWrapper>
  )
}

CommentsDrawer.propTypes = {
  location: PropTypes.object,
}

export default withRouter(CommentsDrawer)
